import { PageFooter } from '@/components/layout/PageFooter';
import { PageHeader } from '@/components/layout/PageHeader';
import { queryClient } from '@/queries/queryClient';
import { useEffect, useRef } from 'react';
import {
  type LoaderFunctionArgs,
  Outlet,
  ScrollRestoration,
  useNavigation,
} from 'react-router-dom';
import LoadingBar, { type LoadingBarRef } from 'react-top-loading-bar';

import { PartnerPageHeader } from '@/components/layout/PartnerPageHeader';
import { useCurrentPartner } from '@/hooks/useCurrentPartner';
import { hasRuntimeConfig } from '@/config/runtime';
import { captureReferenceFromUrl } from '@/lib/captureReference';
import { currentPartnerQuery, extractPartnerName } from '@/queries/currentPartnerQuery';
import { ThemeStyles } from './ThemeStyles';

export async function rootLoader({ params, request }: LoaderFunctionArgs) {
  const { resultId } = params;

  const currentPartner = await queryClient.ensureQueryData(
    currentPartnerQuery(request.url)
  );

  if (!hasRuntimeConfig() && extractPartnerName(request.url) && !currentPartner) {
    throw new Response('Partner not found', { status: 404 });
  }

  // Buffers any provider reference on the landing URL until the order is submitted. Runs after
  // the partner resolves so the storage key is scoped to the partner this visit belongs to.
  captureReferenceFromUrl(request.url);

  return {
    currentPartner,
    resultId,
  };
}

export function Root() {
  const navigation = useNavigation();
  const isLoading = navigation.state !== 'idle';

  const currentPartner = useCurrentPartner();

  useEffect(() => {
    document.documentElement.classList.add(
      currentPartner ? 'bg-app-body' : 'bg-app-primary'
    );

    return () => {
      document.documentElement.classList.remove(
        currentPartner ? 'bg-app-body' : 'bg-app-primary'
      );
    };
  }, [currentPartner]);

  const ref = useRef<LoadingBarRef>(null as never);

  useEffect(() => {
    const current = ref.current;
    if (isLoading) {
      current?.continuousStart();
    } else {
      current?.complete();
    }

    return () => {
      current?.complete();
    };
  }, [isLoading]);

  return (
    <>
      <ThemeStyles />
      <div className="bg-app-primary font-sans text-xl lg:text-2xl">
        {currentPartner ? <PartnerPageHeader /> : <PageHeader />}

        <main className="bg-app-body py-10 xl:pb-20">
          <div className="container relative">
            <Outlet />

            <ScrollRestoration />
          </div>
        </main>

        <PageFooter />
      </div>

      <LoadingBar ref={ref} height={5} color="hsl(var(--color-secondary))" />
    </>
  );
}
